import React from 'react';
import { useAuth } from './AuthProvider';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Wraps routes that require Entra ID authentication.
 * Redirects unauthenticated users to the Entra ID login flow.
 * Shows error state if authentication service is unavailable.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, login, error, sessionExpired } = useAuth();

  if (isLoading) {
    return (
      <div className="auth-loading" role="status" aria-label="Loading">
        <img src="/get-for-me-color-light.svg" alt="GET for Me" style={{ width: '160px', marginBottom: '24px' }} />
        <div className="auth-loading__spinner" />
        <p>Authenticating...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="auth-error" role="alert">
        <img src="/get-for-me-color-light.svg" alt="GET for Me" style={{ width: '160px', marginBottom: '24px' }} />
        <h2>Authentication Error</h2>
        <p>
          {error.includes('unavailable') || error.includes('network')
            ? 'The authentication service is temporarily unavailable. Please try again later.'
            : 'Authentication was unsuccessful. Please try again.'}
        </p>
        <button onClick={() => login()} className="btn btn-primary">
          Try Again
        </button>
      </div>
    );
  }

  if (sessionExpired) {
    return (
      <div className="auth-error" role="alert">
        <img src="/get-for-me-color-light.svg" alt="GET for Me" style={{ width: '160px', marginBottom: '24px' }} />
        <h2>Session Expired</h2>
        <p>Your session has expired. Please log in again.</p>
        <button onClick={() => login()} className="btn btn-primary">
          Log In
        </button>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Trigger Entra ID redirect login
    login();
    return (
      <div className="auth-loading" role="status" aria-label="Redirecting to login">
        <img src="/get-for-me-color-light.svg" alt="GET for Me" style={{ width: '160px', marginBottom: '24px' }} />
        <div className="auth-loading__spinner" />
        <p>Redirecting to login...</p>
      </div>
    );
  }

  return <>{children}</>;
}
